// frontend/src/ts/protocols/studies/components/pendingWritePayloadView.tsx
//
// Renders the Payload column's YAML popover for the PendingWritesTable.
//
// Rendering rules:
// - For `step_update` / `protocol_update`, `row.changes` carries the
//   user-edit-delta — render only the delta's YAML (the full payload
//   is suppressed; unchanged fields are not shown).
// - For `event_create` / `event_delete`, `row.changes` is undefined
//   and the whole payload IS the change — render `row.payload`.
// - Output is wrapped in one `<span>` colored with `--bios-color-success`
//   so the user sees only what changed, highlighted.
// - Empty `changes` (and empty `payload` as a fallback) → `'(empty)'`.

import React from 'react'
import YAML from 'yaml'
import type { PendingWrite } from '@/protocols/persistence/types'

export const CHANGED_FIELD_COLOR = 'var(--bios-color-success)'

/**
 * Multi-line indented YAML. `YAML.stringify` defaults to single-line
 * flow output (`{key: value}`) which is unreadable for nested
 * payloads. `indent: 2` forces block style with newlines + 2-space
 * indentation so the popover wraps cleanly.
 */
export function formatYaml(payload: Record<string, unknown>): string {
    return YAML.stringify(payload, {indent: 2})
}

/**
 * Render only the changed fields (the user's delta) as YAML, wrapped
 * entirely in the success color. Falls back to the full payload when
 * no delta exists (event create / delete). See file header for rules.
 */
export function renderPayloadYaml(row: PendingWrite): React.ReactNode {
    // Prefer the delta when present; otherwise the whole payload is the
    // change (event_create / event_delete). Whichever source is used,
    // everything shown is the change → everything is colored.
    const data = row.changes ?? row.payload
    if (!data || Object.keys(data).length === 0) return '(empty)'
    const yaml = formatYaml(data)
    return (
        <span style={{color: CHANGED_FIELD_COLOR, whiteSpace: 'pre'}}>{yaml}</span>
    )
}

